

import { JExcel } from '../../../../../../shared/components/thirdparty/excelexport/jsegarraexcel.js';
import { getSensorHistoryData } from '../sensors/handlesensordata.js';
import { formatDurationText } from '../../../../../../shared/utils/timeformats.js';
import { SPARE_AXLE } from '../../../../../../shared/utils/global.js';

function buildExcel(rows, unit, wheel) {

  let excel = JExcel.new("Calibri 11 #333333");
  const sheet = 0;

  // Header row
  const headerStyle = excel.addStyle({
    font: "Calibri 12 #333333 B",
    border: "none none thin none",
    align: "C C"
  });

  const headers = [
    "Sent TS Date",
    "Sent TS Time",
    "Sent TS Δ",
    "Received TS Date",
    "Received TS Time",
    "Received TS Δ",
    "Sent-Received (sec)",
    "Sensor Id",
    "Pressure (bar)",
    "Temperature (°C)",
    "Voltage (V)",
    "Rotation",
    "Speed",
    "Map"
  ];

  headers.forEach((l, i) => {
    excel.set(sheet, i, 0, l, headerStyle);
    excel.set(sheet, i, undefined, "auto");
  });

  // Base styles
  const centerStyle = excel.addStyle({ align: "C C" });
  const primaryCenterStyle = excel.addStyle({ font: "Calibri 11 #185785", align: "C C" });

  const dateStyle = excel.addStyle({
    format: "yyyy-mm-dd",
    align: "C C"
  });
  const timeStyle = excel.addStyle({
    format: "hh:mm:ss",
    align: "C C"
  });

  // Navy “block” styles (need separate ones for time cell vs normal cells so formatting stays)
  const primaryColourCellStyle = excel.addStyle({
    font: "Calibri 11 #185785"
  });

  const primaryColourDateStyle = excel.addStyle({
    font: "Calibri 11 #185785",
    format: "yyyy-mm-dd",
    align: "C C"
  });

  const primaryColourTimeStyle = excel.addStyle({
    font: "Calibri 11 #185785",
    format: "hh:mm:ss",
    align: "C C"
  });

  const latencyStyle = excel.addStyle({
    format: "0.00"  // seconds with 2 decimals
  });

  const primaryColourLatencyStyle = excel.addStyle({
    font: "Calibri 11 #185785",
    format: "0.00"
  });

  // --alert - colour: #dc3545;
  // --low - pressure - colour: #e50000;
  // --high - pressure - colour: #320e75;
  // --high - temperature - colour: #ff7e25;
  // --low - voltage - colour: #ffce00;

  const styleLowPressure = excel.addStyle({ font: "Calibri 11 #e50000", format: "0.00" });  // red
  const styleHighPressure = excel.addStyle({ font: "Calibri 11 #320e75", format: "0.00" });  // purple
  const styleHighTemp = excel.addStyle({ font: "Calibri 11 #ff7e25" });  // orange
  const styleLowVoltage = excel.addStyle({ font: "Calibri 11 #ffce00", format: "0.00" });  // yellow

  // Data rows
  let r = 1;
  let prevSensorId = null;
  let isPrimaryColourBlock = false; // first block = default

  let prevSentMs = null;
  let prevRecvMs = null;

  rows.forEach(row => {
    const timeSent = new Date(row.timeSent);
    const timeReceived = new Date(row.timeReceived);
    const sensorId = String(row.sensorId ?? "");

    // Flip block colour ONLY when sensorId changes (after first row)
    if (prevSensorId !== null && sensorId !== prevSensorId) {
      isPrimaryColourBlock = !isPrimaryColourBlock;
    }

    // Choose styles for this row
    const rowCenterStyle = isPrimaryColourBlock ? primaryCenterStyle : centerStyle;

    const rowDateStyle = isPrimaryColourBlock ? primaryColourDateStyle : dateStyle;
    const rowTimeStyle = isPrimaryColourBlock ? primaryColourTimeStyle : timeStyle;
    const rowCellStyle = isPrimaryColourBlock ? primaryColourCellStyle : undefined; // undefined = default

    const sentMs = new Date(row.timeSent).getTime();
    const recvMs = new Date(row.timeReceived).getTime();

    let deltaSentSec = "";
    let deltaRecvSec = "";

    if (Number.isFinite(sentMs) && Number.isFinite(prevSentMs)) {
      deltaSentSec = (prevSentMs - sentMs) / 1000; // newest-on-top
    }
    if (Number.isFinite(recvMs) && Number.isFinite(prevRecvMs)) {
      deltaRecvSec = (prevRecvMs - recvMs) / 1000;
    }

    // update prev
    prevSentMs = sentMs;
    prevRecvMs = recvMs;

    const p = Number(row.pressure);
    const t = Number(row.temperature);
    const v = Number(row.voltage);

    const minP = Number(row.minPressure ?? workingWheel.minPressure);
    const maxP = Number(row.maxPressure ?? workingWheel.maxPressure);
    const maxT = Number(row.maxTemperature ?? workingWheel.maxTemperature);
    const minV = Number(row.minVoltageValue ?? workingWheel.minVoltageValue);

    // choose styles (otherwise fall back to rowCellStyle)
    const pressureStyle =
      (Number.isFinite(minP) && p < minP) ? styleLowPressure :
        (Number.isFinite(maxP) && p > maxP) ? styleHighPressure :
          rowCellStyle;

    const tempStyle =
      (Number.isFinite(maxT) && t > maxT) ? styleHighTemp :
        rowCellStyle;

    const voltStyle =
      (Number.isFinite(minV) && v < minV) ? styleLowVoltage :
        rowCellStyle;

    excel.set(sheet, 0, r, JExcel.toExcelLocalTime(timeSent), rowDateStyle);
    excel.set(sheet, 1, r, JExcel.toExcelLocalTime(timeSent), rowTimeStyle);
    excel.set(sheet, 2, r, formatDurationText(deltaSentSec), rowCenterStyle);
    excel.set(sheet, 3, r, JExcel.toExcelLocalTime(timeReceived), rowDateStyle);
    excel.set(sheet, 4, r, JExcel.toExcelLocalTime(timeReceived), rowTimeStyle);
    excel.set(sheet, 5, r, formatDurationText(deltaRecvSec), rowCenterStyle);

    const sentTimestamp = new Date(row.timeSent).getTime();
    const receivedTimestamp = new Date(row.timeReceived).getTime();

    const latencySec = (Number.isFinite(sentTimestamp) && Number.isFinite(receivedTimestamp))
      ? (receivedTimestamp - sentTimestamp) / 1000
      : "";

    excel.set(sheet, 6, r, latencySec === "" ? "" : Number(latencySec.toFixed(2)),
      isPrimaryColourBlock ? primaryColourLatencyStyle : latencyStyle
    );

    excel.set(sheet, 7, r, row.sensorId, rowCenterStyle);
    excel.set(sheet, 8, r, p, pressureStyle);
    excel.set(sheet, 9, r, t, tempStyle);
    excel.set(sheet, 10, r, v, voltStyle);
    excel.set(sheet, 11, r, Number(row.rotation), rowCellStyle);
    excel.set(sheet, 12, r, row.speed ?? "", rowCellStyle);

    const lat = row.latitude ?? "";
    const lon = row.longitude ?? "";
    const url = (lat && lon) ? `https://www.google.com/maps?q=${lat},${lon}` : "";
    const safeUrl = url.replace(/"/g, '""');

    const mapUrl = url
      ? `=HYPERLINK("${safeUrl}", "Click to view map (${lat},${lon})")`
      : "";
    excel.set(sheet, 13, r, mapUrl, rowCellStyle);

    prevSensorId = sensorId;
    r++;
  });

  const wheelId = typeof wheel === "object" ? wheel.wheelId : wheel;
  excel.generate(`Sensor_Report_${unit.name}_${wheelId}.xlsx`);
}

export async function exportSensorReportExcel(unit, wheel) {

  let workingWheel = wheel;

  if (typeof workingWheel === 'object' && workingWheel !== null && 'wheelId' in workingWheel) {
    const axle = workingWheel.wheelId.slice(1, 3);
    if (axle === SPARE_AXLE.toString()) {
      const s = unit.spareSensors.findIndex(sensor => sensor.wheelId === workingWheel.wheelId);
      workingWheel.minPressure = unit.spareSensors[s]?.minPressure;
      workingWheel.maxPressure = unit.spareSensors[s]?.maxPressure;
      workingWheel.maxTemperature = unit.spareSensors[s]?.maxTemperature;
      workingWheel.minVoltageValue = unit.spareSensors[s]?.minVoltageValue;
    }
  } else if (typeof workingWheel === 'string') {
    const axle = workingWheel.slice(1, 3);
    if (axle === SPARE_AXLE.toString()) {
      const s = unit.spareSensors.findIndex(sensor => sensor.wheelId === workingWheel);
      workingWheel = {
        minPressure: unit.spareSensors[s]?.minPressure,
        maxPressure: unit.spareSensors[s]?.maxPressure,
        maxTemperature: unit.spareSensors[s]?.maxTemperature,
        minVoltageValue: unit.spareSensors[s]?.minVoltageValue,
      };
    }
  }

  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);

  const dateRange = [start.toISOString(), end.toISOString()];

  let rows = await getSensorHistoryData(unit, workingWheel, dateRange, true);

  // sort + cap at 10k (latest)
  rows.sort((a, b) => b.unixTime - a.unixTime);
  if (rows.length > 10000) rows = rows.slice(rows.length - 10000);


  buildExcel(rows, unit, wheel);
}


